import { Component, OnInit } from '@angular/core';
import { ProductService } from '../service/product.service';
import { PopupService } from '../service/popup.service';
import { StateService } from '../service/state.service';
import { DeleteConfirmationDialogComponent } from '../shared/delete-confirmation-dialog/delete-confirmation-dialog.component';

type DeleteTarget = { type: 'category'; category: string } | { type: 'sub-category'; category: string; subCategory: string } | { type: 'product-family'; category: string; subCategory: string; productFamily: string };

@Component({
  selector: 'app-admin-categories',
  templateUrl: './admin-categories.component.html',
  styleUrls: ['./admin-categories.component.css']
})
export class AdminCategoriesComponent implements OnInit {
  categories: string[] = [];
  categorySubCategoriesMap: { [key: string]: string[] } = {};
  subCategoryProductFamiliesMap: { [key: string]: string[] } = {};
  newCategoryInput: string = '';
  newSubCategoryForCategory: string = '';
  newSubCategoryInput: string = '';
  newProductFamilyForSubCategory: string = '';
  newProductFamilyForCategory: string = '';
  newProductFamilyInput: string = '';
  expandedCategory: string | null = null;
  expandedSubCategory: string | null = null;
  editCategory: string | null = null;
  editCategoryInput: string = '';
  editSubCategory: string | null = null;
  editSubCategoryInput: string = '';
  editSubCategoryParent: string | null = null;
  editProductFamily: string | null = null;
  editProductFamilyInput: string = '';
  editProductFamilyParent: string | null = null;
  editProductFamilyParentCategory: string | null = null;
  feedbackMessage: string = '';
  errorMessage: string = '';

  // Delete confirmation dialog state
  showDeleteConfirmation = false;
  deleteTarget: DeleteTarget | null = null;

  constructor(
    private productService: ProductService,
    private popupService: PopupService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
  }

  loadCategories(): void {
    this.productService.getCategories().subscribe({
      next: (cats: string[]) => {
        this.categories = cats.sort();
        // Load sub-categories for each category
        cats.forEach(cat => {
          this.productService.getSubCategories(cat).subscribe({
            next: (subs: string[]) => {
              this.categorySubCategoriesMap[cat] = subs.sort();
              // Load product families for each sub-category
              subs.forEach(sub => {
                this.productService.getProductFamilies(cat, sub).subscribe({
                  next: (families: string[]) => {
                    const key = `${cat}|${sub}`;
                    this.subCategoryProductFamiliesMap[key] = families.sort();
                  },
                  error: (error) => {
                    console.warn(`Failed to load product families for ${sub}:`, error);
                    const key = `${cat}|${sub}`;
                    this.subCategoryProductFamiliesMap[key] = [];
                  }
                });
              });
            },
            error: (error) => {
              console.warn(`Failed to load sub-categories for ${cat}:`, error);
              this.categorySubCategoriesMap[cat] = [];
            }
          });
        });
      },
      error: (error) => {
        console.error('Failed to load categories:', error);
        this.popupService.showDanger('Erreur lors du chargement des catégories.');
      }
    });
  }

  toggleCategory(category: string): void {
    this.expandedCategory = this.expandedCategory === category ? null : category;
  }

  addCategory(): void {
    if (!this.newCategoryInput.trim()) {
      this.errorMessage = 'Veuillez entrer un nom de catégorie.';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }
    this.productService.createCategory(this.newCategoryInput).subscribe({
      next: () => {
        this.categories.push(this.newCategoryInput);
        this.categories.sort();
        this.categorySubCategoriesMap[this.newCategoryInput] = [];
        this.newCategoryInput = '';
        this.feedbackMessage = 'Catégorie créée avec succès.';
        setTimeout(() => this.feedbackMessage = '', 3000);
      },
      error: (error) => {
        console.error('Error creating category:', error);
        this.errorMessage = 'Erreur lors de la création de la catégorie.';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  startEditCategory(category: string): void {
    this.editCategory = category;
    this.editCategoryInput = category;
  }

  saveEditCategory(oldCategory: string): void {
    const newName = this.editCategoryInput.trim();
    if (!newName || newName === oldCategory) {
      this.cancelEditCategory();
      return;
    }
    // Simulate backend update (replace with real API call)
    const idx = this.categories.indexOf(oldCategory);
    if (idx !== -1) {
      this.categories[idx] = newName;
      this.categorySubCategoriesMap[newName] = this.categorySubCategoriesMap[oldCategory] || [];
      delete this.categorySubCategoriesMap[oldCategory];
      this.categories.sort();
      this.feedbackMessage = 'Catégorie modifiée avec succès.';
      setTimeout(() => this.feedbackMessage = '', 3000);
    }
    this.editCategory = null;
    this.editCategoryInput = '';
  }

  cancelEditCategory(): void {
    this.editCategory = null;
    this.editCategoryInput = '';
  }

  confirmDeleteCategory(category: string): void {
    this.deleteTarget = { type: 'category', category };
    this.showDeleteConfirmation = true;
  }

  addSubCategory(): void {
    if (!this.newSubCategoryForCategory) {
      this.errorMessage = 'Veuillez sélectionner une catégorie.';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }
    if (!this.newSubCategoryInput.trim()) {
      this.errorMessage = 'Veuillez entrer un nom de sous-catégorie.';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }
    this.productService.createSubCategory(this.newSubCategoryForCategory, this.newSubCategoryInput).subscribe({
      next: () => {
        if (!this.categorySubCategoriesMap[this.newSubCategoryForCategory]) {
          this.categorySubCategoriesMap[this.newSubCategoryForCategory] = [];
        }
        this.categorySubCategoriesMap[this.newSubCategoryForCategory].push(this.newSubCategoryInput);
        this.categorySubCategoriesMap[this.newSubCategoryForCategory].sort();
        this.newSubCategoryInput = '';
        this.feedbackMessage = 'Sous-catégorie créée avec succès.';
        setTimeout(() => this.feedbackMessage = '', 3000);
      },
      error: (error) => {
        console.error('Error creating sub-category:', error);
        this.errorMessage = 'Erreur lors de la création de la sous-catégorie.';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  startEditSubCategory(sub: string, parent: string): void {
    this.editSubCategory = sub;
    this.editSubCategoryInput = sub;
    this.editSubCategoryParent = parent;
  }

  saveEditSubCategory(oldSub: string, parent: string): void {
    const newName = this.editSubCategoryInput.trim();
    if (!newName || newName === oldSub) {
      this.cancelEditSubCategory();
      return;
    }
    // Simulate backend update (replace with real API call)
    const idx = this.categorySubCategoriesMap[parent].indexOf(oldSub);
    if (idx !== -1) {
      this.categorySubCategoriesMap[parent][idx] = newName;
      this.categorySubCategoriesMap[parent].sort();
      this.feedbackMessage = 'Sous-catégorie modifiée avec succès.';
      setTimeout(() => this.feedbackMessage = '', 3000);
    }
    this.editSubCategory = null;
    this.editSubCategoryInput = '';
    this.editSubCategoryParent = null;
  }

  cancelEditSubCategory(): void {
    this.editSubCategory = null;
    this.editSubCategoryInput = '';
    this.editSubCategoryParent = null;
  }

  confirmDeleteSubCategory(sub: string, parent: string): void {
    this.deleteTarget = { type: 'sub-category', category: parent, subCategory: sub };
    this.showDeleteConfirmation = true;
  }

  addProductFamily(): void {
    if (!this.newProductFamilyForCategory || !this.newProductFamilyForSubCategory) {
      this.errorMessage = 'Veuillez sélectionner une catégorie et une sous-catégorie.';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }
    if (!this.newProductFamilyInput.trim()) {
      this.errorMessage = 'Veuillez entrer un nom de famille de produits.';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }
    this.productService.createProductFamily(this.newProductFamilyForCategory, this.newProductFamilyForSubCategory, this.newProductFamilyInput).subscribe({
      next: () => {
        const key = `${this.newProductFamilyForCategory}|${this.newProductFamilyForSubCategory}`;
        if (!this.subCategoryProductFamiliesMap[key]) {
          this.subCategoryProductFamiliesMap[key] = [];
        }
        this.subCategoryProductFamiliesMap[key].push(this.newProductFamilyInput);
        this.subCategoryProductFamiliesMap[key].sort();
        this.newProductFamilyInput = '';
        this.feedbackMessage = 'Famille de produits créée avec succès.';
        setTimeout(() => this.feedbackMessage = '', 3000);
      },
      error: (error) => {
        console.error('Error creating product family:', error);
        this.errorMessage = 'Erreur lors de la création de la famille de produits.';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  startEditProductFamily(family: string, parentSub: string, parentCat: string): void {
    this.editProductFamily = family;
    this.editProductFamilyInput = family;
    this.editProductFamilyParent = parentSub;
    this.editProductFamilyParentCategory = parentCat;
  }

  saveEditProductFamily(oldFamily: string, parentSub: string, parentCat: string): void {
    const newName = this.editProductFamilyInput.trim();
    if (!newName || newName === oldFamily) {
      this.cancelEditProductFamily();
      return;
    }
    const key = `${parentCat}|${parentSub}`;
    const idx = this.subCategoryProductFamiliesMap[key].indexOf(oldFamily);
    if (idx !== -1) {
      this.subCategoryProductFamiliesMap[key][idx] = newName;
      this.subCategoryProductFamiliesMap[key].sort();
      this.feedbackMessage = 'Famille de produits modifiée avec succès.';
      setTimeout(() => this.feedbackMessage = '', 3000);
    }
    this.editProductFamily = null;
    this.editProductFamilyInput = '';
    this.editProductFamilyParent = null;
    this.editProductFamilyParentCategory = null;
  }

  cancelEditProductFamily(): void {
    this.editProductFamily = null;
    this.editProductFamilyInput = '';
    this.editProductFamilyParent = null;
    this.editProductFamilyParentCategory = null;
  }

  confirmDeleteProductFamily(family: string, parentSub: string, parentCat: string): void {
    this.deleteTarget = { type: 'product-family', category: parentCat, subCategory: parentSub, productFamily: family };
    this.showDeleteConfirmation = true;
  }

  onDeleteConfirm(): void {
    if (!this.deleteTarget) return;
    if (this.deleteTarget.type === 'category') {
      const category = this.deleteTarget.category;
      this.categories = this.categories.filter(cat => cat !== category);
      delete this.categorySubCategoriesMap[category];
      this.feedbackMessage = 'Catégorie supprimée avec succès.';
    } else if (this.deleteTarget.type === 'sub-category' && this.deleteTarget.subCategory) {
      const { category, subCategory } = this.deleteTarget;
      this.categorySubCategoriesMap[category] = this.categorySubCategoriesMap[category].filter(s => s !== subCategory);
      // Also remove from product families map
      const key = `${category}|${subCategory}`;
      delete this.subCategoryProductFamiliesMap[key];
      this.feedbackMessage = 'Sous-catégorie supprimée avec succès.';
    } else if (this.deleteTarget.type === 'product-family' && this.deleteTarget.productFamily) {
      const { category, subCategory, productFamily } = this.deleteTarget;
      const key = `${category}|${subCategory}`;
      this.subCategoryProductFamiliesMap[key] = this.subCategoryProductFamiliesMap[key].filter(f => f !== productFamily);
      this.feedbackMessage = 'Famille de produits supprimée avec succès.';
    }
    this.showDeleteConfirmation = false;
    this.deleteTarget = null;
    setTimeout(() => this.feedbackMessage = '', 3000);
  }

  onDeleteCancel(): void {
    this.showDeleteConfirmation = false;
    this.deleteTarget = null;
  }
}
